import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  Icon,
  Input,
  NumberInput,
  NumberInputField,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stack,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { MdArrowForward, MdInfo } from "react-icons/md";
import {
  TransactionType,
  EstimateSplitFulfillmentStatus,
  ESTIMATE_SPLIT_FULFILLMENT_STATUS_LABELS,
  OrderPayableBy,
  OrderPayableTo,
} from "../../../../views/Orders/enums";
import { isInvalidDiscountAndCredit } from "../../../../utils/util";
import { usePatchEstimateSplit } from "../../../../views/Orders/hooks/usePatchEstimateSplit";
import { DeliveryOption } from "../../types";
import { extractApiErrorMessage } from "../../utils/apiErrorUtils";
import {
  buildEstimateSplitPatchPayload,
  canPersistEstimateSplitConfig,
} from "../../utils/estimateSplitPatchPayload";
import {
  resolveDefaultEstimateSplitTransactionType,
  checkCustomerHasMgpAssociation,
  isMgpDistributorOrOutlet,
  getParentSupplierId,
} from "../../utils/resolveDefaultEstimateSplitTransactionType";
import { isEnquiryWhitelistedForLatestEstimateUi } from "../../utils/isEnquiryWhitelistedForLatestEstimateUi";
import { useGetOrderMediaConfig } from "../../../../views/PlaceOrderTest/queryHooks";
import { useGetAllSuppliers } from "../../../../views/Orders/queryHooks";
import { useUserDetailsContext } from "../../../../context/userDetailsContext";
import { isFacilitationTransactionType } from "../../utils/estimateSingleInvoiceValidation";
import {
  estimateFieldLabelProps,
  estimateSectionHeaderProps,
} from "../../utils/estimateSectionHeaderStyles";
import { SplitPaymentRouteEditor } from "./SplitPaymentRouteEditor";
import { PaymentRouteHelpTooltipContent } from "../utils/PaymentRouteHelpTooltipContent";

const DEFAULT_GARAAZ_SUPPLIER_ID = "64ae87c051c0be36170dc718";
const FULFILLMENT_STATUS_DEBOUNCE_MS = 500;

function isGaraazSupplier(
  supplierId: string,
  garaazSupplierIdFromConfig?: string,
): boolean {
  return (
    !!supplierId &&
    supplierId === (garaazSupplierIdFromConfig || DEFAULT_GARAAZ_SUPPLIER_ID)
  );
}

function sanitizeNonNegativePercentInput(raw: string): string {
  if (raw === "") {
    return "";
  }
  if (raw.includes("-")) {
    return "0";
  }
  const num = Number(raw);
  if (!Number.isNaN(num) && num < 0) {
    return "0";
  }
  return raw;
}

/** Matches part list row separators in PartsListColumn */
const dashedRowSeparator = {
  pb: 2,
  borderBottom: "1px dashed",
  borderColor: "gray.200",
};

interface SplitConfigurationSectionProps {
  estimateNo: string;
  splitId: string;
  supplierId?: string | null;
  orderData?: any;
  /** Enquiry / order number used for v3 UI whitelist checks. */
  orderNo?: string;
  garaazSupplierIdFromConfig?: string;
  isSingleGaraazInvoiceRequired?: boolean;
  transactionType?: string;
  discountPercentage?: number;
  creditNotePercentage?: number;
  fulfillmentStatus?: EstimateSplitFulfillmentStatus;
  paymentRouteFromLabel: string;
  paymentRouteToLabel: string;
  payableBy?: OrderPayableBy;
  payableTo?: OrderPayableTo;
  isExpired: boolean;
  isRemainingItems: boolean;
  onSplitConfigApplied: (update: {
    transactionType?: string;
    discountPercentage?: number;
    creditNotePercentage?: number;
    fulfillmentStatus?: EstimateSplitFulfillmentStatus;
    payableBy?: OrderPayableBy;
    payableTo?: OrderPayableTo;
    /** When omitted, existing delivery options on the split are preserved. */
    deliveryOptions?: DeliveryOption[];
  }) => void;
  isReadOnly?: boolean;
}

export const SplitConfigurationSection: React.FC<
  SplitConfigurationSectionProps
> = ({
  estimateNo,
  splitId,
  supplierId,
  orderData,
  orderNo,
  garaazSupplierIdFromConfig,
  isSingleGaraazInvoiceRequired = false,
  transactionType: transactionTypeProp,
  discountPercentage: discountProp,
  creditNotePercentage: creditProp,
  fulfillmentStatus: fulfillmentStatusProp,
  paymentRouteFromLabel,
  paymentRouteToLabel,
  payableBy: payableByProp,
  payableTo: payableToProp,
  isExpired,
  isRemainingItems,
  onSplitConfigApplied,
  isReadOnly = false,
}) => {
  const patchMutation = usePatchEstimateSplit();
  const [configError, setConfigError] = useState<string | null>(null);
  const { data: configData } = useGetOrderMediaConfig();
  const mgpDistributorConfig = (configData as any)?.mgp_distributor;
  const fulfillmentDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [isFulfillmentPending, setIsFulfillmentPending] = useState(false);

  const isFulfillmentSectionVisible = useMemo(() => {
    return isEnquiryWhitelistedForLatestEstimateUi(
      (configData as any)?.whitelisted_enquiry_nos_for_latest_estimate_ui,
      orderNo,
      orderData,
    );
  }, [configData, orderNo, orderData]);

  const canPersistSplitConfig = useMemo(
    () => canPersistEstimateSplitConfig(estimateNo, splitId),
    [estimateNo, splitId],
  );

  const defaultTransactionType = useMemo(
    () =>
      resolveDefaultEstimateSplitTransactionType({
        supplierId,
        orderData,
        isSingleGaraazInvoiceRequired,
        garaazSupplierIdFromConfig,
        mgpDistributorConfig,
      }),
    [
      supplierId,
      orderData,
      isSingleGaraazInvoiceRequired,
      garaazSupplierIdFromConfig,
      mgpDistributorConfig,
    ],
  );

  const [transactionType, setTransactionType] = useState(
    () => transactionTypeProp || defaultTransactionType || "",
  );
  const [discount, setDiscount] = useState(() =>
    discountProp != null ? String(discountProp) : "",
  );
  const [credit, setCredit] = useState(() =>
    creditProp != null ? String(creditProp) : "",
  );
  const [fulfillmentStatus, setFulfillmentStatus] = useState<
    EstimateSplitFulfillmentStatus | ""
  >(() => fulfillmentStatusProp ?? "");
  const hasClearedTransactionTypeRef = useRef(false);

  useEffect(() => {
    if (transactionTypeProp) {
      hasClearedTransactionTypeRef.current = false;
      setTransactionType(transactionTypeProp);
      return;
    }

    // User cleared TT to "Select" — keep empty; do not re-apply defaults.
    if (hasClearedTransactionTypeRef.current) {
      setTransactionType("");
      return;
    }

    const resolvedDefault = resolveDefaultEstimateSplitTransactionType({
      supplierId,
      orderData,
      isSingleGaraazInvoiceRequired,
      garaazSupplierIdFromConfig,
      mgpDistributorConfig,
    });

    if (isSingleGaraazInvoiceRequired) {
      setTransactionType((prev) =>
        isFacilitationTransactionType(prev)
          ? TransactionType.TRADING
          : prev || TransactionType.TRADING,
      );
      return;
    }

    if (resolvedDefault) {
      setTransactionType(resolvedDefault);
    }
  }, [
    isSingleGaraazInvoiceRequired,
    transactionTypeProp,
    supplierId,
    orderData,
    garaazSupplierIdFromConfig,
    mgpDistributorConfig,
  ]);

  useEffect(() => {
    setDiscount(discountProp != null ? String(discountProp) : "");
  }, [discountProp]);

  useEffect(() => {
    setCredit(creditProp != null ? String(creditProp) : "");
  }, [creditProp]);

  useEffect(() => {
    setFulfillmentStatus(fulfillmentStatusProp ?? "");
  }, [fulfillmentStatusProp]);

  useEffect(() => {
    return () => {
      if (fulfillmentDebounceRef.current) {
        clearTimeout(fulfillmentDebounceRef.current);
      }
    };
  }, []);

  const isSaving =
    patchMutation.isLoading && patchMutation.variables?.splitId === splitId;

  const userDetails = useUserDetailsContext();

  const isUserAllowedToEdit = useMemo(() => {
    const allowedIdsRaw = (configData as any)?.override_edit_user_ids || "";
    if (!allowedIdsRaw) return false;
    const allowedIds = String(allowedIdsRaw)
      .split(",")
      .map((id: string) => id.trim().toLowerCase())
      .filter(Boolean);
    return Boolean(
      userDetails?._id &&
        allowedIds.includes(String(userDetails._id).trim().toLowerCase()),
    );
  }, [configData, userDetails]);

  const { data: allSuppliers } = useGetAllSuppliers();

  const hasMgpAssociation = useMemo(() => {
    return checkCustomerHasMgpAssociation(
      orderData,
      mgpDistributorConfig,
      allSuppliers,
    );
  }, [orderData, mgpDistributorConfig, allSuppliers]);

  const isMgpSupplier = useMemo(() => {
    if (!supplierId) return false;
    const foundSupplier = allSuppliers?.find(
      (s: any) => String(s._id) === String(supplierId),
    );
    const parentSupId = foundSupplier?.parentSupplierId
      ? String(foundSupplier.parentSupplierId)
      : getParentSupplierId(supplierId, orderData);

    return isMgpDistributorOrOutlet(
      supplierId,
      parentSupId,
      mgpDistributorConfig,
    );
  }, [supplierId, allSuppliers, orderData, mgpDistributorConfig]);

  const isForcedFacilitation =
    hasMgpAssociation && isMgpSupplier && !isUserAllowedToEdit;

  const isFacilitationBlocked = useMemo(() => {
    if (!supplierId) return false;
    return (
      isGaraazSupplier(supplierId, garaazSupplierIdFromConfig) ||
      isSingleGaraazInvoiceRequired
    );
  }, [supplierId, garaazSupplierIdFromConfig, isSingleGaraazInvoiceRequired]);

  const isTradingBlocked = useMemo(() => {
    if (isUserAllowedToEdit) {
      return false;
    }
    return isForcedFacilitation;
  }, [isUserAllowedToEdit, isForcedFacilitation]);

  const isTrading = transactionType === TransactionType.TRADING;

  const tradingFieldsValid = useMemo(() => {
    if (!isTrading) return true;
    return !isInvalidDiscountAndCredit(Number(discount), Number(credit));
  }, [isTrading, discount, credit]);

  const applySplitUpdateFromEstimate = useCallback(
    (estimate: any) => {
      const updatedSplit = (estimate?.splits ?? []).find(
        (s: any) => String(s._id) === String(splitId),
      );
      if (!updatedSplit) return;
      onSplitConfigApplied({
        transactionType: updatedSplit.transactionType,
        discountPercentage: updatedSplit.discountPercentage,
        creditNotePercentage: updatedSplit.creditNotePercentage,
        fulfillmentStatus: updatedSplit.fulfillmentStatus,
        payableBy: updatedSplit.payableBy,
        payableTo: updatedSplit.payableTo,
        deliveryOptions: updatedSplit.deliveryOptions ?? [],
      });
      if (updatedSplit.transactionType) {
        setTransactionType(updatedSplit.transactionType);
      }
      if (updatedSplit.discountPercentage != null) {
        setDiscount(String(updatedSplit.discountPercentage));
      } else if (updatedSplit.transactionType !== TransactionType.TRADING) {
        setDiscount("");
      }
      if (updatedSplit.creditNotePercentage != null) {
        setCredit(String(updatedSplit.creditNotePercentage));
      } else if (updatedSplit.transactionType !== TransactionType.TRADING) {
        setCredit("");
      }
      if (updatedSplit.fulfillmentStatus) {
        setFulfillmentStatus(updatedSplit.fulfillmentStatus);
      }
    },
    [splitId, onSplitConfigApplied],
  );

  const persistConfig = useCallback(
    (input: {
      transactionType?: string;
      discountPercentage?: number;
      creditNotePercentage?: number;
      fulfillmentStatus?: EstimateSplitFulfillmentStatus;
    }) => {
      if (!canPersistSplitConfig || isRemainingItems || isReadOnly) return;

      const payload = buildEstimateSplitPatchPayload(input);
      if (
        !payload.transactionType &&
        payload.discountPercentage === undefined &&
        payload.creditNotePercentage === undefined &&
        payload.fulfillmentStatus === undefined &&
        payload.payableBy === undefined &&
        payload.payableTo === undefined
      ) {
        return;
      }

      setConfigError(null);
      patchMutation.mutate(
        { estimateNo, splitId, payload },
        {
          onSuccess: (estimate) => {
            setIsFulfillmentPending(false);
            applySplitUpdateFromEstimate(estimate);
          },
          onError: (error: unknown) => {
            setIsFulfillmentPending(false);
            setConfigError(
              extractApiErrorMessage(error, "Failed to update configuration."),
            );
          },
        },
      );
    },
    [
      canPersistSplitConfig,
      isRemainingItems,
      isReadOnly,
      patchMutation,
      estimateNo,
      splitId,
      applySplitUpdateFromEstimate,
    ],
  );

  const handleTransactionTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      if (!value) {
        hasClearedTransactionTypeRef.current = true;
        setTransactionType("");
        setDiscount("");
        setCredit("");
        // Clearing TT must update parent split state so Push/Proceed gates
        // re-evaluate immediately (local Select alone is not enough).
        onSplitConfigApplied({
          transactionType: undefined,
          discountPercentage: undefined,
          creditNotePercentage: undefined,
          payableBy: undefined,
          payableTo: undefined,
        });
        return;
      }
      hasClearedTransactionTypeRef.current = false;
      setTransactionType(value);
      if (value === TransactionType.FACILITATION) {
        setDiscount("");
        setCredit("");
        persistConfig({ transactionType: value });
        return;
      }
      if (value === TransactionType.TRADING) {
        persistConfig({
          transactionType: value,
          discountPercentage: Number(discount) || 0,
          creditNotePercentage: Number(credit) || 0,
        });
      }
    },
    [persistConfig, discount, credit, onSplitConfigApplied],
  );

  const handleFulfillmentStatusChange = useCallback(
    (value: string) => {
      if (!value) {
        setFulfillmentStatus("");
        return;
      }
      const next = value as EstimateSplitFulfillmentStatus;
      if (next === fulfillmentStatusProp) {
        setFulfillmentStatus(next);
        return;
      }

      setFulfillmentStatus(next);
      // Show loading immediately; debounce the actual PATCH.
      setIsFulfillmentPending(true);
      setConfigError(null);

      if (fulfillmentDebounceRef.current) {
        clearTimeout(fulfillmentDebounceRef.current);
      }
      fulfillmentDebounceRef.current = setTimeout(() => {
        persistConfig({ fulfillmentStatus: next });
      }, FULFILLMENT_STATUS_DEBOUNCE_MS);
    },
    [fulfillmentStatusProp, persistConfig],
  );

  const handleTradingBlur = useCallback(() => {
    if (!isTrading || !tradingFieldsValid || !transactionType) return;

    const discountTrim = discount.trim();
    const creditTrim = credit.trim();
    if (!discountTrim && !creditTrim) {
      return;
    }

    const discountNum = Math.max(0, Number(discountTrim) || 0);
    const creditNum = Number(creditTrim) || 0;
    const savedDiscount = discountProp ?? 0;
    const savedCredit = creditProp ?? 0;
    if (discountNum === savedDiscount && creditNum === savedCredit) {
      return;
    }

    persistConfig({
      transactionType,
      discountPercentage: discountNum,
      creditNotePercentage: creditNum,
    });
  }, [
    isTrading,
    tradingFieldsValid,
    transactionType,
    persistConfig,
    discount,
    credit,
    discountProp,
    creditProp,
  ]);

  const handleDiscountChange = useCallback((valueAsString: string) => {
    setDiscount(sanitizeNonNegativePercentInput(valueAsString));
  }, []);

  const handleDiscountBlur = useCallback(() => {
    if (discount.trim() && Number(discount) < 0) {
      setDiscount("0");
    }
    handleTradingBlur();
  }, [discount, handleTradingBlur]);

  const hasSelectedPaymentRoute =
    paymentRouteFromLabel !== "-" && paymentRouteToLabel !== "-";

  const paymentRouteValue = useMemo(() => {
    if (!transactionType) {
      return {
        kind: "hint" as const,
        text: "Select transaction type first",
      };
    }
    if (hasSelectedPaymentRoute) {
      return { kind: "route" as const };
    }
    return { kind: "empty" as const, text: "—" };
  }, [transactionType, hasSelectedPaymentRoute]);

  if (isRemainingItems) {
    return null;
  }

  return (
    <Box mt={3} pt={3} borderTop="1px solid" borderColor="gray.200">
      <Flex align="center" gap={1} mb={2}>
        <Text {...estimateSectionHeaderProps}>Configuration</Text>
        <Text color="red.500" fontWeight="bold">
          *
        </Text>
      </Flex>

      <VStack align="stretch" spacing={2}>
        <Box {...dashedRowSeparator}>
          <Flex align="flex-end" gap={3} w="100%" flexWrap="nowrap">
            <Box flex="1.4" minW={0} flexShrink={1}>
              <Text {...estimateFieldLabelProps} mb={1}>
                Transaction Type
              </Text>
              {isUserAllowedToEdit || !isForcedFacilitation ? (
                <Select
                  value={transactionType}
                  onChange={handleTransactionTypeChange}
                  isDisabled={isSaving || isReadOnly}
                  size="sm"
                  w="100%"
                  borderRadius="md"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontSize="0.8rem"
                  color="gray.700"
                  fontWeight="600"
                  bg="white"
                  focusBorderColor="blue.400"
                >
                  <option value="">Select</option>
                  {!isFacilitationBlocked && (
                    <option value={TransactionType.FACILITATION}>
                      Facilitation
                    </option>
                  )}
                  {!isTradingBlocked && (
                    <option value={TransactionType.TRADING}>Trading</option>
                  )}
                </Select>
              ) : (
                <Input
                  value={transactionType || "—"}
                  isReadOnly
                  size="sm"
                  w="100%"
                  borderRadius="md"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontSize="0.8rem"
                  color="gray.700"
                  fontWeight="600"
                  bg="white"
                  borderColor="gray.200"
                  cursor="default"
                  _focus={{ borderColor: "gray.200", boxShadow: "none" }}
                />
              )}
            </Box>
            <Box flex="1" minW="64px">
              <Text {...estimateFieldLabelProps} mb={1}>
                Discount (%)
              </Text>
              <NumberInput
                min={0}
                max={100}
                w="100%"
                value={discount}
                onChange={handleDiscountChange}
                onBlur={handleDiscountBlur}
                isDisabled={!isTrading || isSaving || isReadOnly}
                size="sm"
                clampValueOnBlur
              >
                <NumberInputField
                  bg="white"
                  borderRadius="md"
                  placeholder="0"
                />
              </NumberInput>
            </Box>
            <Box flex="1" minW="64px">
              <Text {...estimateFieldLabelProps} mb={1}>
                Credit (%)
              </Text>
              <NumberInput
                min={0}
                w="100%"
                value={credit}
                onChange={(valueAsString) => setCredit(valueAsString)}
                onBlur={handleTradingBlur}
                isDisabled={!isTrading || isSaving || isReadOnly}
                size="sm"
              >
                <NumberInputField
                  bg="white"
                  borderRadius="md"
                  placeholder="0"
                />
              </NumberInput>
            </Box>
          </Flex>
          {!transactionType && (
            <Text
              fontSize="0.7rem"
              color="gray.500"
              mt={1.5}
              fontFamily="'Nunito','Inter', sans-serif"
            >
              Select a transaction type.
            </Text>
          )}
          {isTrading && !tradingFieldsValid && (
            <Text fontSize="0.7rem" color="orange.500" mt={1.5}>
              Enter valid Discount % (0–100) and Credit Note % (≥0).
            </Text>
          )}
        </Box>

        {isFulfillmentSectionVisible && (
          <Box {...dashedRowSeparator}>
            <Flex align="center" gap={1} mb={1.5}>
              <Text {...estimateFieldLabelProps}>Fulfillment</Text>
              {isFulfillmentPending && (
                <Spinner size="xs" color="blue.400" thickness="2px" />
              )}
            </Flex>
            <RadioGroup
              value={fulfillmentStatus}
              onChange={handleFulfillmentStatusChange}
              isDisabled={
                (isSaving && !isFulfillmentPending) ||
                isReadOnly ||
                !canPersistSplitConfig
              }
            >
              <Stack spacing={1.5}>
                {Object.values(EstimateSplitFulfillmentStatus).map((status) => (
                  <Radio
                    key={status}
                    value={status}
                    size="sm"
                    colorScheme="blue"
                    fontFamily="'Nunito','Inter', sans-serif"
                  >
                    <Text
                      as="span"
                      fontSize="0.8rem"
                      color="gray.700"
                      fontWeight="600"
                      fontFamily="'Nunito','Inter', sans-serif"
                    >
                      {ESTIMATE_SPLIT_FULFILLMENT_STATUS_LABELS[status]}
                    </Text>
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
          </Box>
        )}

        <Box {...dashedRowSeparator} borderBottom="none" pb={0}>
          <Flex align="center" justify="space-between" gap={3}>
            <Flex align="center" gap={1}>
              <Text {...estimateFieldLabelProps}>Payment Route</Text>
              <Tooltip
                label={<PaymentRouteHelpTooltipContent />}
                hasArrow
                placement="top"
              >
                <Box as="span" display="inline-flex" cursor="help">
                  <Icon as={MdInfo} boxSize={4} color="gray.500" />
                </Box>
              </Tooltip>
            </Flex>
            <Flex align="center" justify="flex-end" gap={1}>
              {paymentRouteValue.kind === "route" ? (
                <Flex
                  align="center"
                  gap={1}
                  fontSize="0.8rem"
                  color="purple.600"
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="900"
                  lineHeight="1.2"
                  textAlign="right"
                >
                  <Text as="span" fontWeight="800">
                    {paymentRouteFromLabel}
                  </Text>
                  <Icon as={MdArrowForward} boxSize={3.5} />
                  <Text as="span" fontWeight="800">
                    {paymentRouteToLabel}
                  </Text>
                </Flex>
              ) : (
                <Text
                  fontSize="0.75rem"
                  color={
                    paymentRouteValue.kind === "hint"
                      ? "orange.500"
                      : "gray.400"
                  }
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontStyle="italic"
                  fontWeight="600"
                  textAlign="right"
                >
                  {paymentRouteValue.text}
                </Text>
              )}
              {!isReadOnly && canPersistSplitConfig && (
                <SplitPaymentRouteEditor
                  estimateNo={estimateNo}
                  splitId={splitId}
                  payableBy={payableByProp}
                  payableTo={payableToProp}
                  transactionType={transactionType}
                  isExpired={isExpired}
                  onSaved={applySplitUpdateFromEstimate}
                />
              )}
            </Flex>
          </Flex>
        </Box>
      </VStack>

      {configError && (
        <Text fontSize="0.7rem" color="red.500" mt={2}>
          {configError}
        </Text>
      )}
    </Box>
  );
};
